import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, useBreakpointValue, useStyleConfig} from '@chakra-ui/react'
import {Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {ChevronRight, ChevronLeft} from '../../icons/arrows'
import {remCalc} from '../../../theme/_utils'
import {SortingSliderTile} from './sorting-slider-tile'
import {calculateVerticalPadding} from '../utils'
import SanitizedHtml from '../../html-sanitizer'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {debounce} from 'lodash'

export const SortingSlider = ({
    variant = 'white',
    hasPaddings = true,
    hasSlider,
    heading = '',
    bodycopy = '',
    sortingSliderGroup = [],
}) => {
    const isMobile = useBreakpointValue({base: true, md: false})
    const verticalPadding = calculateVerticalPadding('', hasPaddings)

    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('SortingSlider')

    const spacing_16 = remCalc(16)
    const spacing_48 = remCalc(48)
    const spacing_72 = remCalc(72)

    const consistentStatus =
        typeof window !== 'undefined' &&
        ((isMobile && window.innerWidth < 768) || (!isMobile && window.innerWidth >= 768))

    const loadingSpinner = (
        <LoadingSpinner
            wrapperStyles={{
                position: 'relative',
                minH: 'inherit',
                background: 'none',
            }}
            spinnerStyles={{
                top: '50%',
            }}
        />
    )
    const DEBOUNCE_WAIT = 200
    const debouncedSliderGroupResize = debounce((tileGroup) => {
        const tilesLinesHeight = []

        tileGroup.forEach((tile) => {
            let tilelinesHeight = {
                title: 0,
                bodycopy: 0,
                0: 0,
                1: 0,
                2: 0,
                3: 0,
                4: 0,
            }

            Array.from(tile.getElementsByClassName('tile-title')).forEach((tileTitle) => {
                tileTitle.style.minHeight = null
                tilelinesHeight.title = tileTitle.offsetHeight
            })
            Array.from(tile.getElementsByClassName('tile-bodycopy')).forEach((tileBodycopy) => {
                tileBodycopy.style.minHeight = null
                tilelinesHeight.bodycopy = tileBodycopy.offsetHeight
            })
            Array.from(tile.getElementsByClassName('tile-feature')).forEach(
                (tileFeature, index) => {
                    tileFeature.style.minHeight = null
                    tilelinesHeight[index] = tileFeature.offsetHeight
                },
            )

            tilesLinesHeight.push(tilelinesHeight)
        })

        const linesMax = {
            title: Math.max(...tilesLinesHeight.map((item) => item.title)),
            bodycopy: Math.max(...tilesLinesHeight.map((item) => item.bodycopy)),
            0: Math.max(...tilesLinesHeight.map((item) => item[0])),
            1: Math.max(...tilesLinesHeight.map((item) => item[1])),
            2: Math.max(...tilesLinesHeight.map((item) => item[2])),
            3: Math.max(...tilesLinesHeight.map((item) => item[3])),
            4: Math.max(...tilesLinesHeight.map((item) => item[4])),
        }

        tileGroup.forEach((tile) => {
            Array.from(tile.getElementsByClassName('tile-title')).forEach((tileTitle) => {
                tileTitle.style.minHeight = `${linesMax.title}px`
            })

            Array.from(tile.getElementsByClassName('tile-bodycopy')).forEach((tileBodycopy) => {
                tileBodycopy.style.minHeight = `${linesMax.bodycopy}px`
            })

            Array.from(tile.getElementsByClassName('tile-feature')).forEach(
                (tileFeature, index) => {
                    tileFeature.style.minHeight = `${linesMax[index]}px`
                },
            )
        })
    }, DEBOUNCE_WAIT)

    return (
        <Flex
            __css={{...styles.main}}
            data-testid="sorting-slider"
            my={verticalPadding}
            backgroundColor={variant === 'black' ? 'background.dark' : 'background.default'}
            className="del-sorting-slider"
        >
            <Flex {...styles.container}>
                {heading && (
                    <Text
                        color={variant === 'black' ? 'text.inverse' : 'text.default'}
                        {...styles.heading}
                    >
                        {heading}
                    </Text>
                )}
                {bodycopy && (
                    <SanitizedHtml
                        as={Box}
                        html={bodycopy}
                        color={variant === 'black' ? 'text.inverse' : 'text.default'}
                        {...styles.bodycopy}
                    />
                )}
                {hasSlider ? (
                    <Box
                        __css={{...swiperStyles, ...styles.emptyTileContainer}}
                        data-testid="sorting-slider-swiper"
                    >
                        {consistentStatus ? (
                            <Swiper
                                className={'sorting-slider-swiper'}
                                modules={[Navigation, Pagination, Keyboard]}
                                navigation={{
                                    prevEl: `.srt-sld-swiper-button-prev`,
                                    nextEl: `.srt-sld-swiper-button-next`,
                                }}
                                pagination={{
                                    clickable: true,
                                    el: '.swiper-pagination',
                                }}
                                keyboard={{
                                    enabled: true,
                                }}
                                centerInsufficientSlides={true}
                                loop={
                                    isMobile
                                        ? sortingSliderGroup.length > 1
                                        : sortingSliderGroup.length > 4
                                }
                                breakpoints={{
                                    0: {
                                        slidesPerView: 1,
                                        enabled: sortingSliderGroup.length > 1,
                                        spaceBetween: 0,
                                    },
                                    768: {
                                        slidesPerView: 4,
                                        enabled: sortingSliderGroup.length > 4,
                                        spaceBetween: 16,
                                    },
                                    1024: {
                                        slidesPerView: 4,
                                        enabled: sortingSliderGroup.length > 4,
                                        spaceBetween: 24,
                                    },
                                }}
                                onResize={(swiper) => {
                                    debouncedSliderGroupResize(swiper?.slides)
                                }}
                            >
                                {sortingSliderGroup.map((tile, tIndex) => (
                                    <SwiperSlide key={`sorting-tile-${tIndex}`}>
                                        <SortingSliderTile
                                            styles={styles}
                                            tile={tile}
                                            tIndex={tIndex}
                                            variant={variant}
                                        />
                                    </SwiperSlide>
                                ))}
                                <ChevronLeft
                                    className={'srt-sld-swiper-button-prev'}
                                    aria-label="swiper button prev icon"
                                    {...styles.chevronIcon}
                                    left={'0'}
                                    borderColor={
                                        variant === 'black' ? 'text.inverse' : 'text.default'
                                    }
                                />
                                <ChevronRight
                                    className={'srt-sld-swiper-button-next'}
                                    aria-label="swiper button next icon"
                                    {...styles.chevronIcon}
                                    right={'0'}
                                    borderColor={
                                        variant === 'black' ? 'text.inverse' : 'text.default'
                                    }
                                />
                                <Box className={'swiper-pagination'} />
                            </Swiper>
                        ) : (
                            loadingSpinner
                        )}
                    </Box>
                ) : (
                    <Flex
                        data-testid="sorting-slider-noswiper"
                        {...[styles.tilecontainer, styles.emptyTileContainer]}
                    >
                        {consistentStatus
                            ? sortingSliderGroup.map((tile, tIndex) => (
                                  <Box
                                      key={`sorting-tile-${tIndex}`}
                                      w={{
                                          base: '100%',
                                          md: `calc((100% - ${spacing_16}) / 2)`,
                                          lg: `calc((100% - ${spacing_48}) / 3)`,
                                          xl: `calc((100% - ${spacing_72}) / 4)`,
                                      }}
                                  >
                                      <SortingSliderTile
                                          styles={styles}
                                          tile={tile}
                                          tIndex={tIndex}
                                          variant={variant}
                                      />
                                  </Box>
                              ))
                            : loadingSpinner}
                    </Flex>
                )}
            </Flex>
        </Flex>
    )
}

SortingSlider.propTypes = {
    hasPaddings: PropTypes.bool,
    hasSlider: PropTypes.bool,
    variant: PropTypes.string,
    heading: PropTypes.string,
    bodycopy: PropTypes.string,
    sortingSliderGroup: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            body_copy: PropTypes.string,
            image: PropTypes.shape(),
            features: PropTypes.arrayOf(PropTypes.shape({feature: PropTypes.string})),
            button: PropTypes.shape(),
        }),
    ),
}
